import React from 'react'
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeRaw from "rehype-raw";
import rehypeSanitize from 'rehype-sanitize';
import DOMPurify from 'dompurify';
import { SurveyStepContext } from '../../Util/surveyStepContext';
import { defaultSchema } from 'hast-util-sanitize';
import "./SurveyMarkdown.css";
import Markdown from '../../../presentational/Markdown/Markdown';

export interface SurveyMarkdownProps {
  text: string;
  inline?: boolean;
  format?: "Basic" | "GFM";
  renderHtmlCodeBlocksAsHtml?: boolean;
}

export default function SurveyMarkdown(props: SurveyMarkdownProps) {
  const context = React.useContext(SurveyStepContext);
  const format = props.format || context?.surveyDefinition.markdownFormat || "Basic";
  const renderHtmlCodeBlocksAsHtml = props.renderHtmlCodeBlocksAsHtml == false ? false : true;

  if (format === "GFM") {
    // allow tel links through the sanitizer
    const customSanitizationSchema = {
      ...defaultSchema,
      protocols: {
        ...defaultSchema.protocols,
        href: [...(defaultSchema.protocols?.href || []), 'tel'],
      },
    };

    return <div className="survey-markdown survey-markdown-gfm" style={{ display: props.inline ? "inline-block" : "block" }}>
      <ReactMarkdown
        /* Also need to allow tel links here */
        urlTransform={(url) => (url.startsWith('tel:') ? url : defaultUrlTransform(url))}
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitizationSchema]]}
        components={{
          a: props => {
            return <a href={props.href!} target="_blank">{props.children}</a>
          },
          /* Because remirror likes to mess with html, this rendering of html code blocks as actual html 
          allows us to define sections of html that the remirror editor will not render or modify. 
          https://github.com/CareEvolution/MyDataHelpsDesignerUI/pull/1297#issuecomment-2976912554   */
          pre({ children }) {
            const child = Array.isArray(children) ? children[0] : children;
            const childProps = child.props as React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
            if (
              renderHtmlCodeBlocksAsHtml &&
              React.isValidElement(child) &&
              child.type === 'code' &&
              childProps.className == "language-html" &&
              childProps.children
            ) {
              return <div
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(childProps.children?.toString(), { FORBID_TAGS: ['style'], FORBID_ATTR: ['style'] }) }}
              />;
            }
            return <pre>{children}</pre>;
          }
        }}>
        {props.text}
      </ReactMarkdown>
    </div>
  }
  return <div className="survey-markdown survey-markdown-basic" style={{ display: props.inline ? "inline-block" : "block" }}><Markdown text={props.text} inline={props.inline} /></div>
}

